import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';

import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { TokenSession } from '@models/token/token-session';

import { NotificationService } from '@services/utils/notification.service';
import { SessionService } from '@services/utils/session.service';
import { Exceptions } from '@enumerates/exceptions';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private readonly injector = inject(Injector);
    private readonly sessionService = inject(SessionService);
    private readonly router = inject(Router);
    private readonly notificationService = inject(NotificationService);

    private readonly environmentApps = Object.values(environment.apps);

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const easyToken: TokenSession = this.sessionService.getToken();
        const idCenter = this.sessionService.getIdCenter();
        const origin = this.sessionService.getOrigin();

        if (easyToken != null && easyToken.token !== '' && this.environmentApps.some(e => req.url.startsWith(e))) {
            const cloned = req.clone({
                headers: req.headers
                    .append('X-origin', origin)
                    .append('X-accesstoken', easyToken.token)
                    .append('X-idcenter', idCenter.toString())
            });

            return this.buildRequest(cloned, next);
        } else {
            return this.buildRequest(req, next);
        }
    }

    buildRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            tap(
                event => { this.doEvent(req, event); },
                err => {
                    const showErrorMessage = req.headers.get('showErrorMessage');
                    if (showErrorMessage == null || showErrorMessage === 'true') {
                        this.doError(err);
                    }
                }
            )
        );
    }

    private doEvent(req: HttpRequest<any>, event: any): void {
        if (event instanceof HttpResponse) {
            const showSuccessMessage = req.headers.get('showSuccessMessage');
            if (showSuccessMessage) {
                this.notificationService.showSuccessMessage(showSuccessMessage);
            }
        }
    }

    doError(err: any): void {
        const notificationService = this.injector.get(NotificationService);
        if (err instanceof HttpErrorResponse) {
            if (this.isReactivationSessionExpired(err)) {
                this.sessionService.logout();
                this.router.navigateByUrl('/login');

                return;
            }

            if (err.error && !(err.error instanceof ProgressEvent) && err.error.errors) {
                const errors = err.error.errors || err.error;

                if (errors[0].code === Exceptions.emailRequireVerification) {
                    this.router.navigateByUrl('/account/validate/pending');
                } else if (errors[0].code === Exceptions.contractingLinkDisabled) {
                    // El componente de detalle redirige a 'product-unavailable'; no mostrar toast de error.
                } else {
                    errors.forEach((error: { message: any }) => {
                        notificationService.showErrorMessage(error.message);
                    });
                }
            }
        }
    }

    private isReactivationSessionExpired(err: HttpErrorResponse): boolean {
        if (!this.sessionService.getRequiresReactivation()) {
            return false;
        }

        if (err.status === 401) {
            return true;
        }

        if (err.error && !(err.error instanceof ProgressEvent) && err.error.errors) {
            const errors = err.error.errors || err.error;

            return errors[0]?.code === Exceptions.permissionDenied;
        }

        return false;
    }
}
